import { Button, cn, Stack, TextInput, toast } from '@landler/component-library';
import { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PlotOverviewMediaFile } from '@/api/rest/resources/types/plot';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components';
import { Card } from '@/components/widgets/o/Card/Card';
import { useCreatePlotOverview, useRemovePlotOverview, useUpdatePlotOverview } from '@/hooks/usePlotOverview';

import { PlotOverviewFormFields, usePlotOverviewForm } from '../hooks/useControlsForm';
import { ApprovalPill } from './ApprovalPill';
import { MediaFileDialog } from './MediaFileDialog';
import { MediaFiles } from './MediaFiles';

const MAX_DESCRIPTION_LENGTH = 4000;

type PlotOverviewFormInputs = {
  title: string;
  disableTitle?: boolean;
  description: string;
  plotOverviewId?: string;
  isApproved?: boolean;
  mediaFiles: PlotOverviewMediaFile[];
};

export const PlotOverviewForm = ({
  title,
  description,
  disableTitle = false,
  plotOverviewId,
  isApproved,
  mediaFiles,
}: PlotOverviewFormInputs) => {
  const { t } = useTranslation();

  const [isMediaFileDialogOpen, setIsMediaFileDialogOpen] = useState(false);

  const { submit: submitPostPlotOverview, isSubmitting: isPostSubmitting } = useCreatePlotOverview();
  const { submit: submitPatchPlotOverview, isSubmitting: isPatchSubmitting } = useUpdatePlotOverview();
  const { submit: submitRemovePlotOverview, isSubmitting: isRemoveSubmitting } = useRemovePlotOverview();

  const form = usePlotOverviewForm({
    defaultValues: {
      [PlotOverviewFormFields.title]: title ?? '',
      [PlotOverviewFormFields.description]: description ?? '',
    },
  });

  const formTitle = disableTitle ? title : form.watch(PlotOverviewFormFields.title);
  const formDescription = form.watch(PlotOverviewFormFields.description);

  const currentDescriptionLength = formDescription?.length ?? 0;

  const disableCreateButton = !formTitle || !formDescription;
  const disableSaveButton = formTitle === title && formDescription === description;

  const handleSave = async () => {
    if (!plotOverviewId) {
      return;
    }

    const formValues = form.getValues();
    const data = {
      title: formValues[PlotOverviewFormFields.title],
      description: formValues[PlotOverviewFormFields.description],
    };

    await submitPatchPlotOverview(
      {
        id: plotOverviewId,
        ...data,
      },
      () => triggerNotification('update', 'success', t),
      () => triggerNotification('update', 'error', t),
    );
  };

  const handleDelete = async () => {
    if (!plotOverviewId) {
      return;
    }

    await submitRemovePlotOverview(
      plotOverviewId,
      () => {
        triggerNotification('delete', 'success', t);
        form.reset({
          title: disableTitle ? title : '',
          description: '',
        });
      },
      () => triggerNotification('delete', 'error', t),
    );
  };

  const handleCreate = async () => {
    const formValues = form.getValues();
    const data = {
      title: formValues[PlotOverviewFormFields.title],
      description: formValues[PlotOverviewFormFields.description],
    };

    await submitPostPlotOverview(
      data,
      () => triggerNotification('create', 'success', t),
      () => triggerNotification('create', 'error', t),
    );
  };

  const showApprovalPill = plotOverviewId && typeof isApproved === 'boolean';

  return (
    <>
      <Form {...form}>
        <Card
          styles={{
            header: 'typography-h4',
          }}
          header={
            disableTitle ? (
              <Stack direction='row' spacing={2} className='items-center justify-between w-full'>
                <span>{title}</span>
                {showApprovalPill && <ApprovalPill isApproved={isApproved} />}
              </Stack>
            ) : undefined
          }
          body={
            <Stack spacing={6} className='w-full'>
              {!disableTitle && showApprovalPill && (
                <Stack direction='row' spacing={2} className='items-center justify-end w-full'>
                  <ApprovalPill isApproved={isApproved} />
                </Stack>
              )}
              {!disableTitle && (
                <FormField
                  control={form.control}
                  name={PlotOverviewFormFields.title}
                  render={({ field: { value, ...field } }) => (
                    <FormItem className='flex flex-col gap-1'>
                      <FormLabel>{t('shared.mediaUpload.plotOverview.form.fields.title.label')}</FormLabel>
                      <FormControl>
                        <TextInput
                          placeholder={t('shared.mediaUpload.plotOverview.form.fields.title.placeholder')}
                          value={value}
                          disabled={disableTitle}
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name={PlotOverviewFormFields.description}
                render={({ field: { value, ...field } }) => (
                  <FormItem className='flex flex-col gap-2'>
                    <FormLabel>{t('shared.mediaUpload.plotOverview.form.fields.description.label')}</FormLabel>
                    <FormControl>
                      <textarea
                        className={cn(
                          'flex min-h-[200px] w-full rounded-lg border border-neutral-black-12 p-4 placeholder:text-text-disabled',
                          'focus:border-text-primary focus:outline-none',
                          'disabled:cursor-not-allowed disabled:border-text-disabled',
                        )}
                        placeholder={t('shared.mediaUpload.plotOverview.form.fields.description.placeholder')}
                        value={value}
                        maxLength={MAX_DESCRIPTION_LENGTH}
                        {...field}
                      />
                    </FormControl>
                    <span className='typography-caption text-text-disabled'>
                      {t('shared.mediaUpload.plotOverview.labels.characters', {
                        max: MAX_DESCRIPTION_LENGTH,
                        current: currentDescriptionLength,
                      })}
                    </span>
                  </FormItem>
                )}
              />
              <MediaFiles mediaFiles={mediaFiles} plotOverviewId={plotOverviewId} />
              {plotOverviewId ? (
                <Stack direction='row' className='justify-between'>
                  <Button className='bg-error hover:bg-error-dark' onClick={handleDelete} loading={isRemoveSubmitting}>
                    {t('shared.mediaUpload.plotOverview.form.button.delete')}
                  </Button>

                  <Stack direction='row' spacing={4}>
                    <Button onClick={() => setIsMediaFileDialogOpen(true)}>
                      {t('shared.mediaUpload.plotOverview.form.button.addFile')}
                    </Button>

                    <Button onClick={handleSave} loading={isPatchSubmitting} disabled={disableSaveButton}>
                      {t('shared.mediaUpload.plotOverview.form.button.save')}
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Stack direction='row' className='justify-end'>
                  <Button onClick={handleCreate} loading={isPostSubmitting} disabled={disableCreateButton}>
                    {t('shared.mediaUpload.plotOverview.form.button.create')}
                  </Button>
                </Stack>
              )}
            </Stack>
          }
        />
      </Form>
      {plotOverviewId && (
        <MediaFileDialog
          open={isMediaFileDialogOpen}
          onClose={() => setIsMediaFileDialogOpen(false)}
          type='create'
          plotOverviewId={plotOverviewId}
        />
      )}
    </>
  );
};

const triggerNotification = (
  message: 'create' | 'update' | 'delete',
  type: 'success' | 'error',
  t: TFunction<'translation', undefined>,
) => {
  toast({
    title: t(`shared.mediaUpload.plotOverview.form.toast.plotOverview.${type}.${message}`),
    type,
  });
};
